import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  Loader2, Upload, Play, ArrowLeft, Trophy, AlertTriangle,
  Flame, RotateCcw, Video, CheckCircle2, TrendingUp, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";

const SEVERITY_CLASS = {
  low: "border-[#E6FBF5] bg-[#E6FBF5] text-[#06D6A0]",
  medium: "border-[#FEF3C7] bg-[#FEF3C7] text-[#B45309]",
  high: "border-[#FEE2E2] bg-[#FEE2E2] text-[#DC2626]",
};

function SportIcon({ name, className }) {
  const Ico = Icons[name] || Icons.Trophy;
  return <Ico className={className} strokeWidth={2.5} />;
}

export default function Sports() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedSport, setPickedSport] = useState(null); // sport detail for intake
  const [activeSession, setActiveSession] = useState(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/sports/sessions");
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, _] = await Promise.all([
          api.get("/sports"), loadSessions(),
        ]);
        setSports(s.sports || []);
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [loadSessions]);

  // If a session id is present in the URL, open it directly.
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/sports/sessions/${sessionId}`);
        setActiveSession(data.session);
      } catch { /* ignore */ }
    })();
  }, [sessionId]);

  const pickSport = async (slug) => {
    try {
      const { data } = await api.get(`/sports/${slug}`);
      setPickedSport(data.sport);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitIntake = async (answers) => {
    try {
      const { data } = await api.post(`/sports/${pickedSport.slug}/sessions`, { intake: answers });
      setPickedSport(null);
      setActiveSession(data.session);
      navigate(`/app/sports/${data.session.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;
  }

  if (activeSession) {
    return <SessionView
      session={activeSession}
      setSession={setActiveSession}
      onBack={() => { setActiveSession(null); navigate("/app/sports"); loadSessions(); }}
    />;
  }

  return (
    <div className="space-y-10" data-testid="sports-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Sports Coach</p>
        <h1 className="mt-3 font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">
          Get pro-level form feedback from your phone.
        </h1>
        <p className="mt-3 max-w-2xl text-[#718096]">
          Record a short clip of your swing, shot, or technique. Our AI coach breaks it down frame by
          frame and gives you specific fixes + drills to work on today.
        </p>
      </div>

      {sessions.length > 0 && (
        <div data-testid="past-sessions">
          <h2 className="mb-4 flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">
            <RotateCcw className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Your recent analyses
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sessions.slice(0, 6).map((s) => (
              <button key={s.id}
                onClick={() => { setActiveSession(s); navigate(`/app/sports/${s.id}`); }}
                data-testid={`open-session-${s.id}`}
                className="group rounded-2xl border border-[#FFE8E3] bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:shadow-warm">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#C2410C]">{s.sport_label}</p>
                <p className="mt-1 font-nunito text-sm font-bold text-[#2D3748]">
                  {s.analysis?.summary?.slice(0, 60) || `Session · ${s.status}`}
                </p>
                <div className="mt-2 flex items-center gap-2 text-xs text-[#718096]">
                  <span className={`rounded-full px-2 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider ${
                    s.status === "done" ? "bg-[#E6FBF5] text-[#06D6A0]"
                      : s.status === "analyzing" ? "bg-[#FEF3C7] text-[#B45309]"
                      : s.status === "failed" ? "bg-[#FEE2E2] text-[#DC2626]"
                      : "bg-[#FFE8E3] text-[#C2410C]"
                  }`}>{s.status}</span>
                  {s.analysis?.overall_score != null && (
                    <span className="inline-flex items-center gap-1 font-bold text-[#C2410C]">
                      <Trophy className="h-3 w-3" /> {s.analysis.overall_score}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-4 flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">
          <Video className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Pick a sport
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {sports.map((s) => (
            <button key={s.slug} onClick={() => pickSport(s.slug)}
              data-testid={`pick-sport-${s.slug}`}
              className="group flex h-full flex-col rounded-3xl border border-[#FFE8E3] bg-gradient-to-br from-white to-[#FFF9F5] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:shadow-warm">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sunrise text-white shadow-glow-coral">
                <SportIcon name={s.icon} className="h-5 w-5" />
              </span>
              <p className="mt-3 font-nunito text-base font-bold text-[#2D3748]">{s.label}</p>
              <p className="mt-1 font-quicksand text-xs text-[#718096]">{s.tagline}</p>
              {s.quick_tip && (
                <div className="mt-3 flex-1 rounded-xl border border-[#FEF3C7] bg-[#FFFBEB] p-2.5"
                     data-testid={`quick-tip-${s.slug}`}>
                  <p className="mb-0.5 flex items-center gap-1 font-nunito text-[9px] font-bold uppercase tracking-widest text-[#B45309]">
                    <Flame className="h-3 w-3" strokeWidth={2.5} /> Instant tip
                  </p>
                  <p className="font-quicksand text-[11px] leading-snug text-[#92400E]">
                    {s.quick_tip}
                  </p>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {pickedSport && (
        <IntakeModal sport={pickedSport} onClose={() => setPickedSport(null)} onSubmit={submitIntake} />
      )}
    </div>
  );
}

function IntakeModal({ sport, onClose, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const canGo = sport.intake.every((q) => q.type !== "select" || answers[q.key]);

  const submit = async () => {
    setSaving(true);
    try { await onSubmit(answers); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose} data-testid="sport-intake-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Quick intake</p>
        <h3 className="mt-1 font-nunito text-2xl font-extrabold text-[#2D3748]">{sport.label} — where are you at?</h3>
        <p className="mt-1 font-quicksand text-sm text-[#718096]">
          A few quick questions so your coach can give you feedback that actually fits.
        </p>

        <div className="mt-5 space-y-4">
          {sport.intake.map((q) => (
            <div key={q.key}>
              <label className="mb-2 block font-nunito text-sm font-bold text-[#2D3748]">{q.label}</label>
              {q.type === "select" ? (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <button key={opt} type="button"
                      onClick={() => set(q.key, opt)}
                      data-testid={`intake-${q.key}-${opt.slice(0, 10).replace(/\W/g, '')}`}
                      className={`rounded-full border px-3 py-1.5 font-nunito text-xs font-bold transition-all ${
                        answers[q.key] === opt
                          ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                          : "border-[#FFE8E3] bg-white text-[#4A5568] hover:border-[#FF7E67]"
                      }`}>
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <input value={answers[q.key] || ""} onChange={(e) => set(q.key, e.target.value)}
                  placeholder={q.placeholder}
                  data-testid={`intake-${q.key}`}
                  className="h-11 w-full rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose}
            className="rounded-full border border-[#FFE8E3] px-5 py-2 font-nunito text-sm font-bold text-[#718096] hover:bg-[#FFF9F5]">
            Cancel
          </button>
          <button onClick={submit} disabled={!canGo || saving}
            data-testid="intake-submit-btn"
            className="flex-1 rounded-full bg-sunrise py-3 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
            {saving ? "Setting up…" : "Continue to record video →"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function SessionView({ session, setSession, onBack }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [polling, setPolling] = useState(false);
  const [priorDone, setPriorDone] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const fileRef = useRef(null);

  const needsVideo = ["pending_upload", "failed"].includes(session.status);
  const analyzing = ["queued", "analyzing"].includes(session.status);

  // Load prior 'done' sessions of the same sport for the compare picker.
  useEffect(() => {
    if (session.status !== "done") return;
    (async () => {
      try {
        const { data } = await api.get("/sports/sessions");
        const options = (data.sessions || []).filter(
          (s) => s.status === "done" && s.sport === session.sport && s.id !== session.id
        );
        setPriorDone(options);
      } catch { /* ignore */ }
    })();
  }, [session.status, session.sport, session.id]);

  // Poll while analyzing.
  useEffect(() => {
    if (!analyzing) return;
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/sports/sessions/${session.id}`);
        if (data.session.status !== "queued" && data.session.status !== "analyzing") {
          setSession(data.session);
          setPolling(false);
          clearInterval(t);
        } else {
          setSession(data.session);
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => { clearInterval(t); setPolling(false); };
     
  }, [analyzing, session.id]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) {
      toast.error("Video is over 30 MB — trim it or lower the resolution.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post(`/sports/sessions/${session.id}/video`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Video uploaded — coach is reviewing…");
      const { data: fresh } = await api.get(`/sports/sessions/${session.id}`);
      setSession(fresh.session);
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const analysis = session.analysis || {};

  return (
    <div className="space-y-6" data-testid="sport-session-view">
      <button onClick={onBack} data-testid="sports-back-btn"
        className="inline-flex items-center gap-1 font-quicksand text-sm font-semibold text-[#718096] hover:text-[#FF7E67]">
        <ArrowLeft className="h-4 w-4" /> Back to sports
      </button>

      <div className="rounded-[2rem] border border-[#FFE8E3] bg-white p-6 shadow-warm sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">
          {session.sport_label} · Form Analysis
        </p>
        <h2 className="mt-2 font-nunito text-2xl font-extrabold text-[#2D3748]">
          {session.status === "done" ? "Your coach's breakdown" :
            needsVideo ? "Ready to record?" : "Reviewing your video…"}
        </h2>

        {needsVideo && (
          <div className="mt-5 rounded-2xl border border-dashed border-[#FF7E67] bg-[#FFF9F5] p-6 text-center">
            <p className="mb-4 font-quicksand text-sm text-[#4A5568]">
              Record ONE clean rep — the coach only needs a few seconds. Max 30 MB / 60 seconds.
            </p>
            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm"
              onChange={onFile} className="hidden"
              data-testid="video-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="upload-video-btn"
              className="inline-flex items-center gap-2 rounded-full bg-sunrise px-6 py-3 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
              <Upload className="h-4 w-4" strokeWidth={2.5} />
              {uploading ? `Uploading ${progress}%…` : "Upload video"}
            </button>
            {session.status === "failed" && session.error && (
              <p className="mt-4 font-quicksand text-xs text-[#DC2626]">
                Previous attempt failed: {session.error}
              </p>
            )}
          </div>
        )}

        {analyzing && (
          <div className="mt-5 flex items-center gap-3 rounded-2xl border border-[#FEF3C7] bg-[#FFFBEB] p-4">
            <Loader2 className="h-5 w-5 animate-spin text-[#B45309]" />
            <div>
              <p className="font-nunito text-sm font-bold text-[#B45309]">Coach is reviewing your video…</p>
                <p className="mt-2 font-quicksand text-xs text-[#92400E]">This usually takes 30-60 seconds. You can leave and come back.</p>
            </div>
            {polling && <div className="ml-auto text-xs text-[#B45309]">polling</div>}
          </div>
        )}

        {session.status === "done" && (
          <div className="mt-5 space-y-5" data-testid="analysis-result">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-gradient-to-br from-[#FFF9F5] to-[#FFF4E6] p-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-sunrise text-white shadow-glow-coral">
                <span className="font-nunito text-2xl font-extrabold" data-testid="analysis-score">
                  {analysis.overall_score ?? "—"}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#C2410C]">Overall score / 100</p>
                <p className="mt-1 font-quicksand text-sm text-[#2D3748]">{analysis.summary}</p>
              </div>
            </div>

            {analysis.strengths?.length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-1.5 font-nunito text-sm font-bold text-[#06D6A0]">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} /> What you&apos;re doing well
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {analysis.strengths.map((s, i) => (
                    <div key={i} className="rounded-2xl border border-[#D1FAE5] bg-[#E6FBF5]/40 p-3">
                      <p className="font-nunito text-sm font-bold text-[#065F46]">{s.title}</p>
                      <p className="mt-1 font-quicksand text-xs text-[#065F46]/80">{s.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.issues?.length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-1.5 font-nunito text-sm font-bold text-[#DC2626]">
                  <AlertTriangle className="h-4 w-4" strokeWidth={2.5} /> Fixes + drills
                </p>
                <div className="space-y-3">
                  {analysis.issues.map((iss, i) => (
                    <div key={i} className="rounded-2xl border border-[#FFE8E3] bg-white p-4">
                      <div className="flex items-start gap-2">
                        <span className={`rounded-full border px-2 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider ${SEVERITY_CLASS[iss.severity] || SEVERITY_CLASS.medium}`}>
                          {iss.severity}
                        </span>
                        <p className="font-nunito text-sm font-bold text-[#2D3748]">{iss.title}</p>
                      </div>
                      <p className="mt-2 font-quicksand text-sm text-[#4A5568]">{iss.detail}</p>
                      {iss.drill && (
                        <div className="mt-3 rounded-xl bg-[#FFF9F5] p-3">
                          <p className="flex items-center gap-1.5 font-nunito text-xs font-bold text-[#C2410C]">
                            <Flame className="h-3.5 w-3.5" strokeWidth={2.5} /> Drill: {iss.drill.name}
                          </p>
                          <p className="mt-1 font-quicksand text-xs text-[#4A5568]">{iss.drill.how_to}</p>
                          <p className="mt-1 font-nunito text-[11px] font-bold text-[#B45309]">
                            Prescription: {iss.drill.sets}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.progress_focus && (
              <div className="rounded-2xl border border-[#FEF3C7] bg-[#FFFBEB] p-4">
                <p className="flex items-center gap-1.5 font-nunito text-sm font-bold text-[#B45309]">
                  <TrendingUp className="h-4 w-4" strokeWidth={2.5} /> Focus for next session
                </p>
                <p className="mt-1 font-quicksand text-sm text-[#4A5568]">{analysis.progress_focus}</p>
                {analysis.next_session_hint && (
                  <p className="mt-2 font-quicksand text-xs italic text-[#718096]">
                    Tomorrow: {analysis.next_session_hint}
                  </p>
                )}
              </div>
            )}

            {priorDone.length > 0 && !comparison && (
              <div className="rounded-2xl border border-[#FFE8E3] bg-white p-4">
                <p className="flex items-center gap-1.5 font-nunito text-sm font-bold text-[#2D3748]">
                  <TrendingUp className="h-4 w-4 text-[#FF7E67]" strokeWidth={2.5} /> See how far you&apos;ve come
                </p>
                <p className="mt-1 font-quicksand text-xs text-[#718096]">
                  Compare this session to an earlier one — measure real improvement, celebrate what
                  you&apos;ve fixed, and pinpoint what&apos;s still left to work on.
                </p>
                <button onClick={() => setShowCompareModal(true)}
                  data-testid="open-compare-modal"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-sunrise px-4 py-2 font-nunito text-xs font-bold text-white shadow-glow-coral hover:scale-[1.03]">
                  <Trophy className="h-3.5 w-3.5" strokeWidth={2.5} /> Compare with previous session
                </button>
              </div>
            )}

            {comparison && (
              <CompareResult diff={comparison.diff} weeks={comparison.weeks_apart}
                onClose={() => setComparison(null)} />
            )}
          </div>
        )}
      </div>

      {showCompareModal && (
        <CompareModal
          currentSession={session}
          priorDone={priorDone}
          onClose={() => setShowCompareModal(false)}
          onCompared={(cmp) => { setComparison(cmp); setShowCompareModal(false); }}
          comparing={comparing}
          setComparing={setComparing}
        />
      )}
    </div>
  );
}

function CompareModal({ currentSession, priorDone, onClose, onCompared, comparing, setComparing }) {
  const [pickedId, setPickedId] = useState(priorDone[0]?.id || null);
  const [rich, setRich] = useState(false);

  const runCompare = async () => {
    if (!pickedId) return;
    setComparing(true);
    try {
      const { data } = await api.post("/sports/compare", {
        baseline_session_id: pickedId,
        latest_session_id: currentSession.id,
        include_videos: rich,
      });
      toast.success("Comparison ready.");
      onCompared(data.comparison);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setComparing(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose} data-testid="compare-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Progress Compare</p>
        <h3 className="mt-1 font-nunito text-xl font-extrabold text-[#2D3748]">
          Which past session should we compare against?
        </h3>
        <p className="mt-1 font-quicksand text-sm text-[#718096]">
          Pick your baseline. Your coach will pull a side-by-side of what improved and what&apos;s still to work on.
        </p>
        <div className="mt-4 space-y-2">
          {priorDone.map((s) => (
            <button key={s.id} onClick={() => setPickedId(s.id)}
              data-testid={`pick-baseline-${s.id}`}
              className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-all ${
                pickedId === s.id
                  ? "border-[#FF7E67] bg-[#FFF9F5]"
                  : "border-[#FFE8E3] bg-white hover:border-[#FF7E67]"
              }`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sunrise text-white">
                <span className="font-nunito text-sm font-extrabold">{s.analysis?.overall_score ?? "—"}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-nunito text-sm font-bold text-[#2D3748] line-clamp-1">
                  {s.analysis?.summary?.slice(0, 90) || "Prior session"}
                </p>
                <p className="mt-0.5 font-quicksand text-[11px] text-[#718096]">
                  {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
            </button>
          ))}
        </div>

        <label className="mt-4 flex items-center gap-2 rounded-2xl border border-dashed border-[#FFE8E3] bg-[#FFF9F5] p-3">
          <input type="checkbox" checked={rich} onChange={(e) => setRich(e.target.checked)}
            data-testid="compare-rich-checkbox"
            className="h-4 w-4 accent-[#FF7E67]" />
          <span className="font-quicksand text-xs text-[#4A5568]">
            <strong className="font-bold text-[#2D3748]">Deep video compare</strong> — send BOTH videos to your coach for a real visual diff (30-60s slower).
          </span>
        </label>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose}
            className="rounded-full border border-[#FFE8E3] px-4 py-2 font-nunito text-sm font-bold text-[#718096] hover:bg-[#FFF9F5]">
            Cancel
          </button>
          <button onClick={runCompare} disabled={!pickedId || comparing}
            data-testid="run-compare-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-sunrise py-3 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
            {comparing
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Comparing…</>
              : <>Show me the diff →</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function CompareResult({ diff, weeks, onClose }) {
  const delta = diff.score_delta || 0;
  const positive = delta >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-[#FF7E67] bg-white p-5 shadow-warm"
      data-testid="compare-result">
      <div className="mb-3 flex items-start justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#C2410C]">
          Your progress {weeks ? `· ${weeks} week${weeks === 1 ? "" : "s"} apart` : ""}
        </p>
        <button onClick={onClose} className="rounded-full p-1 text-[#718096] hover:bg-[#FFF9F5]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-gradient-to-br from-[#FFF9F5] to-[#FFF4E6] p-4">
        <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-white shadow-glow-coral ${
          positive ? "bg-gradient-to-br from-[#06D6A0] to-[#059669]"
                   : "bg-gradient-to-br from-[#F87171] to-[#DC2626]"}`}>
          <span className="font-nunito text-xl font-extrabold" data-testid="compare-delta">
            {positive ? "+" : ""}{delta}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#C2410C]">
            {positive ? "Score improvement" : "Score change"}
          </p>
          <p className="mt-1 font-quicksand text-sm text-[#2D3748]" data-testid="compare-verdict">
            {diff.verdict}
          </p>
        </div>
      </div>

      {diff.celebration_line && (
        <p className="mt-3 rounded-xl bg-[#FEF3C7] p-3 font-nunito text-sm font-bold text-[#B45309]"
           data-testid="compare-celebration">
          🎉 {diff.celebration_line}
        </p>
      )}

      {diff.standout_wins?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 font-nunito text-sm font-bold text-[#06D6A0]">Standout wins this session</p>
          <ul className="space-y-1">
            {diff.standout_wins.map((w, i) => (
              <li key={i} className="flex items-start gap-2 font-quicksand text-sm text-[#4A5568]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#06D6A0]" strokeWidth={2.5} />
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {diff.resolved_issues?.length > 0 && (
          <div className="rounded-2xl border border-[#D1FAE5] bg-[#E6FBF5]/50 p-3">
            <p className="mb-1 font-nunito text-sm font-bold text-[#065F46]">✓ Resolved</p>
            <ul className="space-y-1.5">
              {diff.resolved_issues.map((r, i) => (
                <li key={i} className="text-xs text-[#065F46]/80">
                  <span className="font-bold">{r.title}</span>
                  {r.note && <> — {r.note}</>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {diff.persisting_issues?.length > 0 && (
          <div className="rounded-2xl border border-[#FEF3C7] bg-[#FFFBEB] p-3">
            <p className="mb-1 font-nunito text-sm font-bold text-[#B45309]">Still working on</p>
            <ul className="space-y-1.5">
              {diff.persisting_issues.map((p, i) => (
                <li key={i} className="text-xs text-[#92400E]">
                  <span className="font-bold">{p.title}</span>
                  {p.next_step && <> — {p.next_step}</>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {diff.new_issues?.length > 0 && (
        <div className="mt-3 rounded-2xl border border-[#FEE2E2] bg-[#FEF2F2] p-3">
          <p className="mb-1 font-nunito text-sm font-bold text-[#DC2626]">New issues to watch</p>
          <ul className="space-y-1.5">
            {diff.new_issues.map((n, i) => (
              <li key={i} className="text-xs text-[#7F1D1D]">
                <span className="font-bold">{n.title}</span> ({n.severity}) — {n.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {diff.next_focus && (
        <div className="mt-4 rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-3">
          <p className="flex items-center gap-1.5 font-nunito text-sm font-bold text-[#C2410C]">
            <TrendingUp className="h-4 w-4" strokeWidth={2.5} /> Next focus
          </p>
          <p className="mt-1 font-quicksand text-sm text-[#4A5568]">{diff.next_focus}</p>
        </div>
      )}
    </motion.div>
  );
}
